import { useEffect, useState, useCallback } from 'react';
import toast from 'react-hot-toast';
import { AlertCircle } from 'lucide-react';
import { useTodayStore } from '../../store/todayStore';
import { useAuthStore } from '../../store/authStore';
import { useTaskStore } from '../../store/taskStore';
import { PendingRequests } from '../../components/requests';
import { TodayHero } from '../../components/today/TodayHero';
import { FundraisingCard } from '../../components/today/FundraisingCard';
import { AgendaList } from '../../components/today/AgendaList';
import { NeedsAttention } from '../../components/today/NeedsAttention';
import { JumpBackIn } from '../../components/today/JumpBackIn';
import { QuickActionBar } from '../../components/today/QuickActionBar';

/**
 * TodayView — the rep's action-first morning screen. Greeting + glance, the
 * overdue→today agenda, "needs attention" deep-links, and jump-back-in chips.
 * Self-scoped via GET /api/dashboard/today.
 */
export const TodayView = () => {
  const { user, canViewAllPeople } = useAuthStore();
  const { data, isLoading, error, fetch } = useTodayStore();
  const completeTask = useTaskStore((s) => s.completeTask);

  const [doneIds, setDoneIds] = useState<Set<string>>(new Set());
  const [completingIds, setCompletingIds] = useState<Set<string>>(new Set());

  useEffect(() => {
    fetch();
  }, [fetch]);

  const handleComplete = useCallback(
    async (id: string) => {
      setCompletingIds((s) => new Set(s).add(id));
      try {
        await completeTask(id);
        setDoneIds((s) => new Set(s).add(id));
        toast.success('Task completed');
      } catch {
        toast.error('Could not complete task');
      } finally {
        setCompletingIds((s) => {
          const next = new Set(s);
          next.delete(id);
          return next;
        });
      }
    },
    [completeTask],
  );

  if (!user) return null;

  if (isLoading && !data) {
    return (
      <div className="space-y-6">
        <div className="card p-6 animate-pulse">
          <div className="h-4 w-24 bg-gray-100 dark:bg-gray-800 rounded mb-3" />
          <div className="h-7 w-56 bg-gray-100 dark:bg-gray-800 rounded" />
        </div>
        <div className="card p-4 animate-pulse space-y-3">
          {[1, 2, 3, 4].map((i) => (
            <div key={i} className="h-12 bg-gray-100 dark:bg-gray-800 rounded-lg" />
          ))}
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 rounded-lg p-4 flex items-start gap-3">
        <AlertCircle className="w-5 h-5 text-red-500 dark:text-red-400 flex-shrink-0 mt-0.5" />
        <p className="text-sm text-red-700 dark:text-red-300">{error}</p>
      </div>
    );
  }

  if (!data) return null;

  const agenda = data.agenda.filter((i) => !doneIds.has(i.id));

  return (
    <div className="space-y-6">
      <TodayHero glance={data.glance} userName={user.displayName || user.email} followUpDue={data.attention.followUpDue} />

      <FundraisingCard fundraising={data.fundraising} />

      {/* Managers only — decisions first, ahead of the attention/agenda flow */}
      {canViewAllPeople() && (
        <div className="card p-4">
          <PendingRequests variant="widget" />
        </div>
      )}

      {/* One priority-ordered column on every breakpoint: attention → agenda →
          jump-back-in. The Today view is a feed, not a data page — no side rail. */}
      <NeedsAttention attention={data.attention} />

      <AgendaList
        items={agenda}
        onComplete={handleComplete}
        completingIds={completingIds}
        followUpDue={data.attention.followUpDue}
      />

      <JumpBackIn favorites={data.jumpBackIn.favorites} recent={data.jumpBackIn.recent} />

      <QuickActionBar />
    </div>
  );
};
